import { useState, type ChangeEvent, type FormEvent, type ReactNode } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { StrategyCardBody } from "./StrategyCardBody";
import {
  StrategyCardEditBody,
  buildStrategyEditBody,
  buildStrategyEditDraft,
  isStrategyEditSaveDisabled,
  type StrategyCardEditBodyField,
  type StrategyEditDraft,
} from "./StrategyCardEditBody";
import { StrategyOpportunitiesList } from "./StrategyOpportunitiesList";
import type { SeoStrategyEditBody, SeoStrategyRecommendation } from "@/types/seo-strategy";

/**
 * WEB-004 (`components/seo-strategy.md § The screen`): "the card takes the edit session as one
 * optional prop and renders the header with a title slot (h4 or Input), the edit body and the
 * footer -- a refinement of the ADR's decision 3, not a mode threaded through the read body."
 * Shape chosen here (recorded in the task's transition log): the save callback receives the whole
 * edit body built from the draft, the cancel callback takes nothing, and `isSaving` disables `Save`
 * alongside the draft's own validity. Which cards are editing, the kebab item and the mutation
 * itself are `WEB-005`'s.
 */
export interface StrategyCardEditSession {
  onSave: (body: SeoStrategyEditBody) => void;
  onCancel: () => void;
  isSaving: boolean;
}

interface StrategyCardProps {
  recommendation: SeoStrategyRecommendation;
  /**
   * Whatever the parent passes, rendered in the order passed. The card renders no
   * button, menu or badge of its own choosing beyond what this slot supplies
   * (`decisions/the-strategy-card-is-a-shell-with-an-action-slot.md`); a card rendered
   * with no `actions` is read-only by construction.
   */
  actions?: ReactNode;
  onStartTest?: (keyword?: string) => void;
  /** Present only when the grouping is "suggested" and can still be added. */
  onAddGroupingToMasterList?: () => void;
  /** Present only while this card is being edited. Absent, the card renders exactly as before. */
  editSession?: StrategyCardEditSession;
}

/**
 * The strategy card is a shell over `StrategyCardBody` / `StrategyCardEditBody` and
 * `StrategyOpportunitiesList`. It decides its own border from `status` alone, and nothing else
 * about its own state -- the browser (`WEB-006`) computes the actions slot per state, including
 * withholding it entirely for a `viewer`.
 */
export function StrategyCard({
  recommendation,
  actions,
  onStartTest,
  onAddGroupingToMasterList,
  editSession,
}: StrategyCardProps) {
  const isDismissed = recommendation.status === "dismissed";
  const isEditing = editSession !== undefined;

  // The draft is local state of the editing card, created from the item when the session starts
  // (`components/seo-strategy.md § The screen`, "the draft lives here and dies with it"). This
  // card does not remount when `editSession` appears or disappears (WEB-005 keeps one card per
  // id), so the draft is (re)seeded here, during render, whenever `isEditing` flips -- the React
  // "adjusting state when a prop changes" pattern: a `setState` call made while rendering is
  // applied before the browser paints, with no extra render or effect delay.
  const [sessionActive, setSessionActive] = useState(isEditing);
  const [draft, setDraft] = useState<StrategyEditDraft | null>(
    isEditing ? buildStrategyEditDraft(recommendation) : null,
  );
  if (isEditing !== sessionActive) {
    setSessionActive(isEditing);
    setDraft(isEditing ? buildStrategyEditDraft(recommendation) : null);
  }
  // Guards the render below against the one throwaway pass React makes with the old `draft`
  // immediately after the `setDraft` call above; never persisted.
  const currentDraft = draft ?? (isEditing ? buildStrategyEditDraft(recommendation) : null);

  const handleDraftChange = <K extends StrategyCardEditBodyField>(key: K, value: StrategyEditDraft[K]) => {
    setDraft((previous) => (previous ? { ...previous, [key]: value } : previous));
  };

  const handleTitleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    setDraft((previous) => (previous ? { ...previous, gapTitle: value } : previous));
  };

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!editSession || !currentDraft) return;
    editSession.onSave(buildStrategyEditBody(currentDraft, recommendation));
  };

  const titleElement =
    isEditing && currentDraft ? (
      <Input
        aria-label="Strategy title"
        className="h-11 text-lg font-semibold"
        autoFocus
        value={currentDraft.gapTitle}
        onChange={handleTitleChange}
      />
    ) : (
      <h4 className="break-words text-lg font-semibold text-foreground">{recommendation.gapTitle}</h4>
    );

  const content = (
    <>
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0 flex-1 space-y-1.5">
          <div className="flex flex-wrap items-center gap-2">{titleElement}</div>

          {isDismissed ? (
            <>
              <p className="text-sm text-muted-foreground">Reason: {recommendation.dismissReason}</p>
              {recommendation.dismissNotes ? (
                <p className="text-sm text-muted-foreground">{recommendation.dismissNotes}</p>
              ) : null}
            </>
          ) : null}

          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground">
            <span>
              <span className="font-semibold text-foreground">Source:</span> Amazon AI Score
            </span>
            <span>
              <span className="font-semibold text-foreground">Author:</span> Signal AI
            </span>
          </div>
        </div>

        <div className="flex shrink-0 items-center gap-2">{actions}</div>
      </div>

      {isEditing && currentDraft ? (
        <StrategyCardEditBody recommendation={recommendation} draft={currentDraft} onChange={handleDraftChange} />
      ) : (
        <StrategyCardBody recommendation={recommendation} onAddGroupingToMasterList={onAddGroupingToMasterList} />
      )}

      {isEditing && editSession && currentDraft ? (
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" size="sm" type="button" onClick={editSession.onCancel}>
            Cancel
          </Button>
          <Button
            variant="default"
            size="sm"
            type="submit"
            disabled={isStrategyEditSaveDisabled(currentDraft) || editSession.isSaving}
          >
            Save
          </Button>
        </div>
      ) : isDismissed ? null : (
        <StrategyOpportunitiesList recommendation={recommendation} onStartTest={onStartTest} />
      )}
    </>
  );

  return (
    <Card variant={recommendation.status === "prioritized" ? "ai" : "ai-plain"}>
      {isEditing ? (
        <form className="space-y-6 p-6" onSubmit={handleSubmit}>
          {content}
        </form>
      ) : (
        <CardContent className="space-y-6 p-6">{content}</CardContent>
      )}
    </Card>
  );
}
